import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ArrowRight, ArrowLeft, Save } from "lucide-react";
import { PROGRAM_LABELS, Disclaimer, CoachBadge } from "@/components/program/shared";

const Field = ({ q, value, onChange }) => {
  const cls = "bg-black/40 border-white/10 text-white";
  if (q.type === "textarea")
    return <Textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={3} className={cls} data-testid={`q-${q.key}`} />;
  if (q.type === "select" || q.type === "yes_no")
    return (
      <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={`q-${q.key}`}
        className="w-full h-11 rounded-lg bg-black/40 border border-white/10 text-white px-3 text-sm">
        <option value="">Select…</option>
        {(q.options || (q.type === "yes_no" ? ["yes", "no"] : [])).map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
      </select>
    );
  if (q.type === "checkbox") {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div className="flex flex-wrap gap-2" data-testid={`q-${q.key}`}>
        {(q.options || []).map((o) => {
          const on = arr.includes(o);
          return <button type="button" key={o} onClick={() => onChange(on ? arr.filter((x) => x !== o) : [...arr, o])}
            className={`px-3 py-2 rounded-lg text-xs font-mono border ${on ? "border-cyan-400/70 bg-cyan-400/10 text-cyan-300" : "border-white/10 text-slate-400"}`}>{o.replace(/_/g, " ")}</button>;
        })}
      </div>
    );
  }
  if (q.type === "tags") {
    const str = Array.isArray(value) ? value.join(", ") : (value ?? "");
    return <Input value={str} onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} placeholder="comma, separated" className={cls} data-testid={`q-${q.key}`} />;
  }
  if (q.type === "slider") {
    return <div className="flex items-center gap-3"><input type="range" min="1" max="10" value={value ?? 5} onChange={(e) => onChange(Number(e.target.value))} className="flex-1 accent-cyan-400" data-testid={`q-${q.key}`} /><span className="font-mono text-cyan-300 w-6 text-center">{value ?? 5}</span></div>;
  }
  return <Input type={q.type === "number" || q.type === "measurement" ? "number" : (q.type === "date" ? "date" : "text")}
    value={value ?? ""} onChange={(e) => onChange(q.type === "number" || q.type === "measurement" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
    className={cls} data-testid={`q-${q.key}`} />;
};

export const Onboarding = ({ access, onDone }) => {
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const [{ data: qs }, { data: ob }] = await Promise.all([
        api.get(`/programs/questionnaire?program_type=${access.program_type}`),
        api.get(`/programs/onboarding/${access.id}`),
      ]);
      setQuestions(qs);
      setAnswers(ob.answers || {});
      setStep(ob.step || 0);
    })();
  }, [access.id, access.program_type]);

  if (!questions.length) return <div className="pt-40 text-center font-mono text-xs text-cyan-300 animate-pulse">PREPARING YOUR INTAKE…</div>;

  const sections = [...new Set(questions.map((q) => q.section))];
  const sectionQs = questions.filter((q) => q.section === sections[step]);
  const set = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));

  const persist = async (submit = false, nextStep = step) => {
    await api.post(`/programs/onboarding/${access.id}`, { answers, step: nextStep, submit });
  };
  const saveLater = async () => { setSaving(true); await persist(false); toast.success("Saved — resume anytime"); setSaving(false); };

  const missing = sectionQs.filter((q) => q.required && (answers[q.key] === undefined || answers[q.key] === "" || (Array.isArray(answers[q.key]) && !answers[q.key].length)));

  const next = async () => {
    if (missing.length) { toast.error("Please complete the required fields"); return; }
    if (step < sections.length - 1) { const ns = step + 1; setStep(ns); await persist(false, ns); window.scrollTo(0, 0); }
    else {
      setSaving(true);
      const { data } = await api.post(`/programs/onboarding/${access.id}`, { answers, step, submit: true });
      setSaving(false);
      if (data.blocked || data.review) { toast.message("Our team will review your intake first"); }
      onDone();
    }
  };

  return (
    <div className="pt-28 pb-24 px-4 sm:px-8 max-w-2xl mx-auto min-h-screen bg-[#04060b]" data-testid="onboarding">
      <div className="flex items-center justify-between mb-2">
        <div className="mono-label">Welcome to your CG Labz Program</div>
        <div className="text-[11px] font-mono text-slate-500" data-testid="onboarding-step">STEP {step + 1} OF {sections.length}</div>
      </div>
      <h1 className="font-serif text-3xl sm:text-4xl text-white mb-1">Let&apos;s build your program.</h1>
      <p className="text-sm text-slate-400 mb-6">{PROGRAM_LABELS[access.program_type]} · {access.duration_weeks} weeks</p>

      <div className="h-1.5 rounded-full bg-white/10 mb-8 overflow-hidden">
        <div className="h-full bg-cyan-400 transition-all" style={{ width: `${((step + 1) / sections.length) * 100}%` }} />
      </div>

      <div className="glass rounded-2xl p-6" data-testid={`onboarding-section-${step}`}>
        <div className="serif-display text-2xl text-white mb-5">{sections[step]}</div>
        <div className="space-y-5">
          {sectionQs.map((q) => (
            <div key={q.key}>
              <label className="text-[12px] text-slate-300 mb-1.5 block">{q.label}{q.required && <span className="text-rose-400"> *</span>}</label>
              <Field q={q} value={answers[q.key]} onChange={(v) => set(q.key, v)} />
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between mt-6 gap-3">
        <div className="flex gap-2">
          {step > 0 && <Button variant="outline" onClick={() => setStep(step - 1)} data-testid="onboarding-back"><ArrowLeft size={14} className="mr-1" /> Back</Button>}
          <Button variant="outline" onClick={saveLater} disabled={saving} data-testid="onboarding-save"><Save size={14} className="mr-1" /> Save &amp; continue later</Button>
        </div>
        <Button onClick={next} disabled={saving} className="btn-primary rounded-lg" data-testid="onboarding-next">
          {step < sections.length - 1 ? "Continue" : "Build my program"} <ArrowRight size={14} className="ml-2" />
        </Button>
      </div>
      <Disclaimer className="mt-8" />
    </div>
  );
};
